import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { IOrder, IOrderForm } from '../../Types/Orders';
import useApi from '../../hook/UseApi';
import { CircularProgress } from '@mui/material';

const RU_PHONE_PATTERN = /^\+7\d{10}$/;

interface OrderModalProps {
    onClose: () => void;
    productId: string;
    setNotification: () => void;
    api: ReturnType<typeof useApi<IOrder>>;
}

const OrderModal: React.FC<OrderModalProps> = ({
    onClose,
    productId,
    setNotification,
    api,
}) => {
    const [submitError, setSubmitError] = useState<string | null>(null);
    const {
        handleSubmit,
        register,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<IOrderForm>();

    const onSubmitForm = async (formdata: IOrderForm) => {
        setSubmitError(null);
        try {
            const order = JSON.stringify({ ...formdata, product: productId });
            await api.createOrder(order);
            reset();
            onClose();
            setNotification();
        } catch (error: any) {
            const message = Array.isArray(error?.message)
                ? error.message.join(', ')
                : error?.message || 'Не удалось отправить заявку';
            setSubmitError(message);
        }
    };

    return (
        <div
            className="order-modal-overlay"
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <div className="order-modal" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="order-modal-close">
                    &times;
                </button>

                <div className="order-modal-content">
                    <div className="order-modal-left">
                        <h2 className="order-modal-title">
                            Хотите оформить данный заказ?
                        </h2>
                        <p className="order-modal-description">
                            Для этого заполните форму ниже, и мы свяжемся с вами
                            в ближайшее время.
                        </p>
                    </div>

                    <div className="order-modal-right">
                        <form
                            className="order-form"
                            onSubmit={handleSubmit(onSubmitForm)}
                        >
                            <input
                                type="text"
                                placeholder="ВАШЕ ИМЯ"
                                className="order-input"
                                {...register('userName', {
                                    required: 'Имя обязательно',
                                    minLength: {
                                        value: 2,
                                        message: 'Имя от 2 символов',
                                    },
                                })}
                            />
                            {errors.userName && (
                                <p style={{ color: 'red' }}>
                                    {errors.userName.message?.toString()}
                                </p>
                            )}
                            <input
                                type="tel"
                                {...register('tel', {
                                    required: 'Номер телефона обязателен',
                                    pattern: {
                                        value: RU_PHONE_PATTERN,
                                        message:
                                            'Формат: +7 и 10 цифр, например +79001234567',
                                    },
                                })}
                                placeholder="+79001234567"
                                className="order-input"
                            />
                            {errors.tel && (
                                <p style={{ color: 'red' }}>
                                    {errors.tel.message?.toString()}
                                </p>
                            )}
                            <input
                                type="email"
                                {...register('email', {
                                    required: 'Почта обязательна',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Некорректный почтовый адрес',
                                    },
                                })}
                                placeholder="ВАШ EMAIL"
                                className="order-input"
                            />
                            {errors.email && (
                                <p style={{ color: 'red' }}>
                                    {errors.email.message?.toString()}
                                </p>
                            )}
                            {submitError && (
                                <p style={{ color: 'red' }}>{submitError}</p>
                            )}
                            <button
                                type="submit"
                                className="order-submit-btn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <CircularProgress
                                        size={23}
                                        style={{ color: 'white' }}
                                    />
                                ) : (
                                    'ОТПРАВИТЬ ЗАЯВКУ'
                                )}
                            </button>
                            <p className="order-privacy-text">
                                Отправляя свои данные, вы подтверждаете свое
                                согласие на обработку и хранение данных в
                                соответствии с{' '}
                                <a
                                    href="/privacy-policy"
                                    className="privacy-link"
                                >
                                    Политикой конфиденциальности
                                </a>
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderModal;
